/**
 * Phase 8 · C-49 · Shared viz — TatGauge.
 *
 * Semicircular SVG gauge: 180° arc, 140×80 px.
 * Colour: green ≤ 60% of target, amber ≤ 90%, red above or when breach=true.
 * Centre label: `{days}/{target} d`.
 */
import './tokens.css';

export interface TatGaugeProps {
  days: number;
  target: number;
  breach?: boolean;
}

const W = 140;
const H = 80;
const CX = W / 2;
const CY = H;
const R = 60;

function polar(angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + R * Math.cos(rad), y: CY + R * Math.sin(rad) };
}

// Arc path from 180° (left) sweeping to (180 + 180*t)°. t in [0..1].
function arcPath(t: number) {
  const end = polar(180 + 180 * Math.max(0, Math.min(1, t)));
  const start = polar(180);
  const largeArc = t > 0.5 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${R} ${R} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

function gaugeColor(days: number, target: number, breach?: boolean): string {
  if (breach) return 'var(--viz-danger)';
  const safeTarget = target > 0 ? target : 1;
  const ratio = days / safeTarget;
  if (ratio <= 0.6) return 'var(--viz-success)';
  if (ratio <= 0.9) return 'var(--viz-warning)';
  return 'var(--viz-danger)';
}

export function TatGauge({ days, target, breach = false }: TatGaugeProps) {
  const safeTarget = target > 0 ? target : 1;
  const fillRatio = Math.max(0, Math.min(1, days / safeTarget));
  const color = gaugeColor(days, target, breach);
  const label = `${days}/${target} d`;

  return (
    <div
      className="inline-flex flex-col items-center gap-1"
      role="img"
      aria-label={`TAT ${days} of ${target} days${breach ? ' (breached)' : ''}`}
    >
      <svg width={W} height={H + 6} viewBox={`0 0 ${W} ${H + 6}`} className="overflow-visible">
        <path d={arcPath(1)} fill="none" stroke="var(--viz-border)" strokeWidth={8} strokeLinecap="round" />
        <path d={arcPath(fillRatio)} fill="none" stroke={color} strokeWidth={8} strokeLinecap="round" />
        <text
          x={CX}
          y={CY - 10}
          textAnchor="middle"
          fontSize="15"
          fontWeight={600}
          fill="var(--viz-text)"
          className="tabular-nums"
        >
          {label}
        </text>
      </svg>
    </div>
  );
}
